import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { RADIAL_BAR_FILLS } from 'src/features/home/types';
import PieChart from 'src/features/statistic/components/chart/PieChart';
import { PaymentByMachineStatistic } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { CURRENCY } from 'src/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    currency: CURRENCY;
    isLoading: boolean;
    data?: PaymentByMachineStatistic[];
};
export default function WidgetPaymentByMachine({ isLoading, currency, data }: Props) {
    const { formatMessage, formatNumber } = useIntl();

    const columns: ColumnDef<PaymentByMachineStatistic>[] = [
        {
            accessorKey: 'machine_name',
            header: formatMessage(statisticMessages.machine),
        },
        {
            accessorKey: 'total_amount',
            header: formatMessage(statisticMessages.total),
            cell: ({ row }) => {
                return formatNumber(row.original.total_amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
        {
            accessorKey: 'transactions_count',
            header: formatMessage(statisticMessages.transactions),
        },
        {
            accessorKey: 'average_amount',
            header: formatMessage(statisticMessages.average),
            cell: ({ row }) => {
                return formatNumber(Number(row.original.average_amount) / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
    ];

    return (
        <GeneralBlock className="min-h-110 flex-1">
            <SectionBlockTitle>{formatMessage(statisticMessages.paymentsByMachine)}</SectionBlockTitle>
            {isLoading ? (
                <Skeleton className="h-107" />
            ) : data && data.length > 0 ? (
                <>
                    <div className="flex h-full w-full justify-center gap-2 px-10">
                        <PieChart data={data} dataKey="total_amount" />
                        <div className="flex flex-col gap-1">
                            {data?.map((entry, i) => {
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <div
                                            style={{
                                                backgroundColor: RADIAL_BAR_FILLS[i % RADIAL_BAR_FILLS.length],
                                                width: 10,
                                                height: 10,
                                                borderRadius: 50,
                                            }}
                                        />
                                        <span className="w-24 truncate">{entry.machine_name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <StatisticTable
                        title={formatMessage(statisticMessages.breakdown)}
                        data={data}
                        columns={columns as any}
                    />
                </>
            ) : (
                <GeneralMessageBlock
                    icon={ICON_MAP.SEARCH}
                    title={formatMessage(statisticMessages.noPaymentsFound)}
                    subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
                />
            )}
        </GeneralBlock>
    );
}
